import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { SubmitterDetails } from '@/components/common/SubmitterDetails';
import { teacherDetailQueryOptions } from '../api/teacherSubmissions.api';
import {
  TeacherProfileDetails,
  TeacherProfileDetailsSkeleton,
} from '../components/TeacherProfileDetails';
import { TEACHER_PROFILE_SCROLL_AREA_CLASS } from '../constants/teacherSubmission.constants';
import { useLastViewedTeacherStore } from '../store/lastViewedTeacher.store';

const TEACHER_DETAIL_TITLE = 'Teacher details';

interface TeacherDetailBodyProps {
  teacherId: number;
  onShowOnlyThisInstitute: (instituteId: number) => void;
}

function TeacherDetailBody({ teacherId, onShowOnlyThisInstitute }: TeacherDetailBodyProps) {
  const {
    data: teacher,
    error,
    refetch,
    isFetching,
  } = useQuery(teacherDetailQueryOptions(teacherId));

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load this teacher"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
      />
    );
  }

  if (!teacher) return <TeacherProfileDetailsSkeleton />;

  return (
    <TeacherProfileDetails
      teacher={teacher}
      onShowOnlyThisInstitute={() => onShowOnlyThisInstitute(teacher.institute.id)}
    />
  );
}

interface TeacherSubmissionDetailPageProps {
  teacherId: number;
}

export function TeacherSubmissionDetailPage({ teacherId }: TeacherSubmissionDetailPageProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const { data: teacher } = useQuery(teacherDetailQueryOptions(teacherId));
  const setLastViewedTeacherId = useLastViewedTeacherStore((state) => state.setLastViewedTeacherId);

  useEffect(() => {
    setLastViewedTeacherId(teacherId);
  }, [teacherId, setLastViewedTeacherId]);

  function handleBack() {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({ to: '/settings/teacher-submissions' });
  }

  function handleShowOnlyThisInstitute(instituteId: number) {
    void navigate({ to: '/settings/teacher-submissions', search: { instituteId } });
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <header className="flex flex-col gap-2">
        <PageTitle leading={<BackButton onClick={handleBack} />}>{TEACHER_DETAIL_TITLE}</PageTitle>
        <PageToolbar currentTitle={TEACHER_DETAIL_TITLE} />
        {teacher?.appUpdatedByName && teacher.appUpdatedAt && (
          <SubmitterDetails
            name={teacher.appUpdatedByName}
            hrEmpId={teacher.appUpdatedByHrEmpId}
            amName={teacher.appUpdatedByAmName}
            submittedAt={teacher.appUpdatedAt}
          />
        )}
      </header>

      <div className={TEACHER_PROFILE_SCROLL_AREA_CLASS}>
        <TeacherDetailBody
          teacherId={teacherId}
          onShowOnlyThisInstitute={handleShowOnlyThisInstitute}
        />
      </div>
    </div>
  );
}
